import { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
  Button,
  ButtonText,
  ChevronLeftIcon,
  ChevronRightIcon,
  IconButton,
  Pill,
  TrashIcon,
} from '@utilities';

import styles from './_index.module.scss';

const SpanBlock = ({ canMoveLeft, canMoveRight, onMoveSpan, onRemoveSpan, onUpdateSpan, span }) => {
  const [netName, setNetName] = useState('');
  const [selectedKeys, setSelectedKeys] = useState([]);

  const selectedPoints = span.points.filter(({ key }) => selectedKeys.includes(key));
  // "All Others" can't be a net member: it's the complement of the span's
  // explicit values, so combining it would export a column matching nobody.
  const canCombine =
    selectedPoints.length >= 2 && !selectedPoints.some(({ isAllOthers }) => isAllOthers);
  const singleSelectedNet =
    selectedPoints.length === 1 && selectedPoints[0].isNet ? selectedPoints[0] : null;

  // Text typed for one operation must not carry over to another (e.g. a combine
  // name silently becoming a rename), so the field resets when the bar's mode
  // or target net changes.
  const netBarMode = singleSelectedNet ? `rename-${singleSelectedNet.key}` : 'combine';
  useEffect(() => {
    setNetName('');
  }, [netBarMode]);

  const handleToggleChip = (pointKey) => {
    setSelectedKeys((prev) =>
      prev.includes(pointKey) ? prev.filter((key) => key !== pointKey) : [...prev, pointKey]
    );
  };

  const handleCombine = () => {
    onUpdateSpan(span.id, { name: netName, pointKeys: selectedKeys, type: 'combine' });
    setNetName('');
    setSelectedKeys([]);
  };

  const handleRename = () => {
    onUpdateSpan(span.id, { name: netName, pointKey: singleSelectedNet.key, type: 'rename' });
    setNetName('');
    setSelectedKeys([]);
  };

  const handleDissolve = () => {
    onUpdateSpan(span.id, { pointKey: singleSelectedNet.key, type: 'dissolve' });
    setNetName('');
    setSelectedKeys([]);
  };

  const handleRemovePoint = (pointKey) => {
    setSelectedKeys((prev) => prev.filter((key) => key !== pointKey));
    onUpdateSpan(span.id, { pointKey, type: 'remove-point' });
  };

  return (
    <div className={styles['banner-canvas-block']}>
      <span aria-hidden className={styles['banner-canvas-grip']} draggable>
        ⠿
      </span>
      <div className={styles['banner-canvas-block-content']}>
        <div className={styles['banner-canvas-block-header']}>
          <h5 title={span.label}>{span.label}</h5>
          <IconButton
            aria-label={`Move ${span.label} left`}
            data-testid={`move-span-left-${span.id}`}
            isDisabled={!canMoveLeft}
            onClick={() => onMoveSpan(-1)}
            tooltip="Move left"
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            aria-label={`Move ${span.label} right`}
            data-testid={`move-span-right-${span.id}`}
            isDisabled={!canMoveRight}
            onClick={() => onMoveSpan(1)}
            tooltip="Move right"
          >
            <ChevronRightIcon />
          </IconButton>
          <IconButton
            aria-label={`Remove ${span.label} from banner`}
            data-testid={`remove-span-${span.id}`}
            onClick={() => onRemoveSpan(span.id)}
            tooltip="Remove from banner"
          >
            <TrashIcon />
          </IconButton>
        </div>
        <div className={styles['banner-canvas-chips']}>
          {span.points.map((point) => (
            <Pill
              className={classNames(styles['banner-canvas-chip'], {
                [styles['banner-canvas-chip-net']]: point.isNet,
              })}
              data-testid={`banner-chip-${point.key}`}
              isActive={selectedKeys.includes(point.key)}
              isBold={point.isNet}
              isModified={span.points.length > 1}
              key={point.key}
              onClick={() => handleToggleChip(point.key)}
              onClose={() => handleRemovePoint(point.key)}
              title={point.label}
              value={point.label}
            />
          ))}
        </div>
        {(canCombine || singleSelectedNet) && (
          <div className={styles['banner-canvas-net-bar']}>
            <input
              aria-label={canCombine ? 'Net name' : 'Rename net'}
              className={styles['banner-canvas-net-input']}
              onChange={({ target }) => setNetName(target.value)}
              placeholder={
                canCombine ? 'Net name (optional)' : `Rename "${singleSelectedNet.label}"`
              }
              type="text"
              value={netName}
            />
            {canCombine && (
              <Button onClick={handleCombine} text="Combine into net" variant="secondary" />
            )}
            {singleSelectedNet && (
              <>
                <Button
                  isDisabled={!netName.trim()}
                  onClick={handleRename}
                  text="Rename"
                  variant="secondary"
                />
                {Boolean(singleSelectedNet.memberPoints?.length) && (
                  <ButtonText onClick={handleDissolve}>Dissolve net</ButtonText>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

SpanBlock.propTypes = {
  canMoveLeft: PropTypes.bool.isRequired,
  canMoveRight: PropTypes.bool.isRequired,
  onMoveSpan: PropTypes.func.isRequired,
  onRemoveSpan: PropTypes.func.isRequired,
  onUpdateSpan: PropTypes.func.isRequired,
  span: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    points: PropTypes.arrayOf(
      PropTypes.shape({
        isNet: PropTypes.bool,
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        memberPoints: PropTypes.array,
      })
    ).isRequired,
  }).isRequired,
};

const BannerCanvas = ({ banner, onRemoveSpan, onReorderSpans, onUpdateSpan }) => {
  const [dragIndex, setDragIndex] = useState(null);

  const handleMove = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= banner.length || fromIndex === toIndex) return;

    const reordered = [...banner];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    onReorderSpans(reordered);
  };

  const handleDragStart = (event, index) => {
    // Firefox won't start a drag without data; the payload itself is unused.
    event.dataTransfer.setData('text/plain', '');
    event.dataTransfer.effectAllowed = 'move';
    // The drag originates from the grip, but the ghost should be the whole block.
    event.dataTransfer.setDragImage(event.currentTarget, 16, 16);
    setDragIndex(index);
  };

  const handleDrop = (dropIndex) => {
    if (dragIndex === null) return;

    handleMove(dragIndex, dropIndex);
    setDragIndex(null);
  };

  if (!banner.length) {
    return (
      <div className={styles['banner-canvas-empty']}>
        <p>No banner columns yet.</p>
        <p>Check a cut in the left rail to add banner columns.</p>
      </div>
    );
  }

  return (
    <div className={styles['banner-canvas']}>
      {banner.map((span, index) => (
        <div
          key={span.id}
          onDragEnd={() => setDragIndex(null)}
          onDragOver={(event) => event.preventDefault()}
          onDragStart={(event) => handleDragStart(event, index)}
          onDrop={() => handleDrop(index)}
        >
          <SpanBlock
            canMoveLeft={index > 0}
            canMoveRight={index < banner.length - 1}
            onMoveSpan={(delta) => handleMove(index, index + delta)}
            onRemoveSpan={onRemoveSpan}
            onUpdateSpan={onUpdateSpan}
            span={span}
          />
        </div>
      ))}
    </div>
  );
};

BannerCanvas.propTypes = {
  banner: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRemoveSpan: PropTypes.func.isRequired,
  onReorderSpans: PropTypes.func.isRequired,
  onUpdateSpan: PropTypes.func.isRequired,
};

export default BannerCanvas;
